import { useEffect, useState } from 'react'
import { harvestDates, hatcheries,
  type HarvestDate, type Hatchery } from '../api'
import { dagMnd } from './Varsel'
import { useT } from '../lang'

/* The sheet that opens when a badge lands somewhere.
 *
 * ONE SHEET FOR FOUR DESTINATIONS, because they are one act with four
 * addresses. From the walkway, "these fish go to merd 5", "these fish go on the
 * boat" and "these fish are being deloused" are the same gesture pointed at
 * different things — and a product that made them three different screens would
 * be organising itself by our tables rather than by their Tuesday.
 *
 * THREE TAPS FOR THE ORDINARY CASE. They moved everything, yesterday or today.
 * So the date is two buttons before it is a picker, "Alt" is preselected, and
 * the reason is optional chips rather than a required dropdown.
 *
 * WHY THE TARGET IS ALREADY DECIDED WHEN THIS OPENS. The gesture said it — the
 * badge is sitting on merd 5. A form that then asks "which pen?" would be
 * asking the reader to repeat, in words, something they have just done with
 * their hand, and that is the difference between a product and a spreadsheet
 * with rounded corners.
 *
 * WHAT IT DOES NOT ASK. Not how many fish, unless somebody chooses to say. It
 * is not established that a crew knows the number at the moment they open a
 * net, and a field that must be filled with an estimate is a field that gets
 * filled with a lie. The column exists; the question is optional.
 */

export type Maal = 'pen' | 'harvest' | 'treatment' | 'stock'

/** What a move was for. Optional, and chips because the answer is always one of
 *  four and typing it would be slower than the move itself. */
const GRUNNER = ['treatment', 'grading', 'merge', 'harvest'] as const

/** The five in `lice_treatments_method_check`. Not a free field: the report
 *  names one of these and a typo would be a filing that does not parse. */
const METODER = ['termisk', 'mekanisk', 'ferskvann', 'kaldtvann',
                 'medikament'] as const

const today = () => new Date().toISOString().slice(0, 10)
const yesterday = () => new Date(Date.now() - 86400000)
  .toISOString().slice(0, 10)

export interface Svar {
  on: string
  /** Harvest only: the boat that took them, when one was picked. */
  vessel_name?: string | null
  mmsi?: number | null
  part: 'all' | 'some'
  n_fish: number | null
  reason: string | null
  /** Treatment only. */
  method?: string
  /** Stocking only. */
  name?: string
  origin?: string | null
}

export function Flyttark({
  maal, siteId, fra, til, gruppe, opptatt, gruppeId, fraPenId,
  busy, onCancel, onGo,
}: {
  maal: Maal
  siteId: string
  /** The ring the fish are leaving, as a number a person reads. Absent when
   *  they are standing nowhere — which happens after a displacement. */
  fra: string | null
  til: string
  gruppe: string
  /** The group already standing in the target, if any. Its presence is what
   *  makes this a merge or a displacement, and the sheet asks rather than
   *  letting somebody discover it afterwards. */
  opptatt: string | null
  /** Harvest only: which population, in which ring. Both are needed to ask
   *  for boats — see `harvestDates`. */
  gruppeId?: string | null
  fraPenId?: string | null
  busy: boolean
  onCancel: () => void
  onGo: (v: Svar & { displace: boolean }) => void
}) {
  const t = useT()
  const [on, setOn] = useState(today())
  const [part, setPart] = useState<'all' | 'some'>('all')
  const [antall, setAntall] = useState('')
  const [reason, setReason] = useState<string | null>(null)
  const [method, setMethod] = useState<string>('termisk')
  const [navn, setNavn] = useState(`H${String(new Date().getFullYear()).slice(2)}`)
  const [origin, setOrigin] = useState('')
  /* Only asked when it has to be. A displacement and a merge are different
     Tuesdays, and only the person who was there knows which. */
  const [displace, setDisplace] = useState(false)
  const [anlegg, setAnlegg] = useState<Hatchery[]>([])
  /* The boats that actually called here, offered as slaughter dates.
     `null` while we do not know yet, `[]` once we know there are none — a
     spinner and an empty list are different sentences. */
  const [baater, setBaater] = useState<HarvestDate[] | null>(null)
  const [baat, setBaat] = useState<HarvestDate | null>(null)
  /** The first day these fish stood in this ring. Nothing before it can be a
   *  slaughter date, so nothing before it is reachable. */
  const [fraDag, setFraDag] = useState<string | null>(null)

  /* WHY A LIST OF BOATS AND NOT A DATE FIELD.
     Fish do not leave a ring on their own — a harvest is a wellboat alongside
     for most of a day. So the possible slaughter dates are not every day since
     the smolt went in; they are the handful of days a boat was here, and AIS
     already knows those. It also answers the question the date field cannot:
     a crew remembers "the week Hordagut was here" long after they have stopped
     remembering the 17th. */
  useEffect(() => {
    if (maal !== 'harvest') return
    let dead = false
    void harvestDates(siteId, gruppeId, fraPenId)
      .then((r) => {
        if (dead) return
        setBaater(r.available ? r.candidates : [])
        setFraDag(r.from ?? null)
        // A group graded into this ring today can only be harvested today.
        // Moving the field rather than letting somebody pick a day the
        // receiver will refuse.
        if (r.from && on < r.from) setOn(r.from)
      })
      .catch(() => { if (!dead) setBaater([]) })
    return () => { dead = true }
  }, [maal, siteId, gruppeId, fraPenId])

  useEffect(() => {
    if (maal !== 'stock') return
    let dead = false
    void hatcheries(siteId).then((r) => { if (!dead) setAnlegg(r.items) })
      .catch(() => { /* a free-text field is a fine fallback */ })
    return () => { dead = true }
  }, [maal, siteId])

  const tittel = maal === 'harvest' ? t('To the slaughterhouse')
    : maal === 'treatment' ? t('Delousing')
      : maal === 'stock' ? t('Put fish in')
        : `${t('Move')} ${gruppe || t('fish')}`

  return (
    <div className="card stack flyttark" style={{ gap: 14 }}>
      <div className="row" style={{ justifyContent: 'space-between',
                                    alignItems: 'baseline', gap: 12 }}>
        <h3 style={{ margin: 0, fontSize: 16 }}>
          {tittel}
          <span className="faint" style={{ fontWeight: 400 }}>
            {'  '}
            {maal === 'pen' && fra && <>{t('Pen')} {fra} → </>}
            {maal === 'stock' ? <>{t('Pen')} {til}</>
              : maal === 'pen' ? <>{t('Pen')} {til}</>
                : <>{t('Pen')} {fra}</>}
          </span>
        </h3>
        <button className="btn ghost sm" onClick={onCancel}>{t('Cancel')}</button>
      </div>

      {/* THE ONE QUESTION ONLY THE PERSON THERE CAN ANSWER. Said before the
          move, not discovered after it. */}
      {maal === 'pen' && opptatt && (
        <div className="stack" style={{ gap: 8 }}>
          <div className="notice">
            {t('Pen')} {til} {t('already holds')} <strong>{opptatt}</strong>.
          </div>
          <div className="seg sm" role="tablist">
            <button type="button" role="tab" aria-selected={!displace}
              onClick={() => setDisplace(false)}>{t('Merge them')}</button>
            <button type="button" role="tab" aria-selected={displace}
              onClick={() => setDisplace(true)}>{t('Move those out')}</button>
          </div>
          <span className="faint" style={{ fontSize: 12 }}>
            {displace
              ? t('%s comes out and waits for a pen. You place it next.')
                .replace('%s', opptatt)
              : t('The two become one population.')}
          </span>
        </div>
      )}

      {/* EVERY DESTINATION ASKS WHEN. A move recorded on the wrong day puts
          fish in a ring they were not in, and every count taken in the window
          is then attributed to the wrong population. */}
      <div className="felt">
        <span>{t('When?')}</span>
        <div className="seg sm" role="tablist">
          {/* "Yesterday" is not offered when these fish were not here
              yesterday. A shortcut that produces a refusal is not a
              shortcut. */}
          {!(fraDag && yesterday() < fraDag) && (
            <button type="button" role="tab" aria-selected={on === yesterday()}
              onClick={() => setOn(yesterday())}>{t('Yesterday')}</button>
          )}
          <button type="button" role="tab" aria-selected={on === today()}
            onClick={() => setOn(today())}>{t('Today')}</button>
        </div>
        {/* Floored at the day these fish entered this ring. The receiver
            refuses anything earlier, and a field that lets somebody choose a
            day it will then reject is a field that wasted their time. */}
        <input type="date" value={on} max={today()}
          min={fraDag ?? undefined}
          onChange={(e) => setOn(e.target.value)} />
        {fraDag && fraDag > yesterday() && (
          <span className="faint" style={{ fontSize: 12 }}>
            {t('%s has stood here since %s.')
              .replace('%s', gruppe).replace('%s', dagMnd(fraDag))}
          </span>
        )}
      </div>

      {maal === 'stock' && (
        <>
          <label className="felt">
            <span>{t('What are they called?')}</span>
            <input value={navn} maxLength={60} style={{ maxWidth: 200 }}
              onChange={(e) => setNavn(e.target.value)} />
          </label>
          <label className="felt">
            <span>{t('From which hatchery?')}{' '}
              <span className="faint">{t('optional')}</span></span>
            {/* A datalist and not a dropdown of 302. The ones that have shipped
                to this farm come first from the receiver, so the right answer
                is usually the first keystroke. */}
            <input list="settefisk" value={origin} maxLength={200}
              style={{ maxWidth: 260 }}
              onChange={(e) => setOrigin(e.target.value)} />
            <datalist id="settefisk">
              {anlegg.map((h) => (
                <option key={`${h.locality_no}-${h.name}`} value={h.name}>
                  {h.operator ?? ''}
                </option>
              ))}
            </datalist>
          </label>
        </>
      )}

      {maal === 'treatment' && (
        <div className="felt">
          <span>{t('Method')}</span>
          <div className="brikker">
            {METODER.map((mth) => (
              <button key={mth} type="button"
                className={`brikke${method === mth ? ' on' : ''}`}
                onClick={() => setMethod(mth)}>{t(`method:${mth}`)}</button>
            ))}
          </div>
        </div>
      )}

      {maal === 'pen' && (
        <div className="felt">
          <span>{t('How much?')}</span>
          <div className="seg sm" role="tablist">
            <button type="button" role="tab" aria-selected={part === 'all'}
              onClick={() => setPart('all')}>{t('All of it')}</button>
            <button type="button" role="tab" aria-selected={part === 'some'}
              onClick={() => setPart('some')}>{t('Part of it')}</button>
          </div>
          {/* Only when they said "part of it", and even then not required.
              A split without a number is still a split. */}
          {part === 'some' && (
            <div className="stack" style={{ gap: 4, marginTop: 6 }}>
              <input inputMode="numeric" placeholder={t('number of fish')}
                value={antall} style={{ maxWidth: 160 }}
                onChange={(e) => setAntall(e.target.value)} />
              <span className="faint" style={{ fontSize: 12 }}>
                {t('Leave it blank if you do not know.')}
              </span>
            </div>
          )}
        </div>
      )}

      {maal === 'pen' && (
        <div className="felt">
          <span>{t('Why?')} <span className="faint">{t('optional')}</span></span>
          <div className="brikker">
            {GRUNNER.map((g) => (
              <button key={g} type="button"
                className={`brikke${reason === g ? ' on' : ''}`}
                onClick={() => setReason(reason === g ? null : g)}>
                {t(`why:${g}`)}
              </button>
            ))}
          </div>
        </div>
      )}

      {maal === 'harvest' && (baater === null || baater.length > 0) && (
        <div className="felt">
          <span>{t('Which boat took them?')}</span>
          {baater === null ? (
            <p className="faint" style={{ margin: 0, fontSize: 12 }}>
              {t('Looking for boats…')}
            </p>
          ) : (
            <div className="baatliste">
              {baater.map((b) => (
                <button type="button" key={`${b.on}:${b.mmsi ?? ''}`}
                  className={`baatvalg${baat?.on === b.on
                    && baat?.mmsi === b.mmsi ? ' valgt' : ''}`}
                  onClick={() => {
                    const same = baat?.on === b.on && baat?.mmsi === b.mmsi
                    setBaat(same ? null : b)
                    if (!same) setOn(b.on)
                  }}>
                  <strong>{b.vessel_name ?? t('Unnamed vessel')}</strong>
                  <span className="faint sml">
                    {dagMnd(b.on)}
                    {b.dwell_min !== null && b.dwell_min >= 30
                      && <>{' · '}{Math.round(b.dwell_min / 60)}{' '}
                        {t('h alongside')}</>}
                  </span>
                  {/* WHY THIS DAY IS BEING OFFERED. Seven bare dates is a
                      guessing game; the register's own reading of each call
                      is what makes the top one the obvious answer. */}
                  <span className="faint sml">
                    {b.grunn === 'harvest'
                      ? t('lay here, then went to a slaughterhouse')
                      : b.grunn === 'unexplained'
                        ? t('the register does not say why it came')
                        : b.method || t(`event:${b.event_type}`)}
                  </span>
                </button>
              ))}
            </div>
          )}
          <p className="faint" style={{ margin: '8px 0 0', fontSize: 12 }}>
            {t('Or set the date yourself below, if the boat is not listed.')}
          </p>
        </div>
      )}

      {maal === 'harvest' && (
        <p className="faint" style={{ margin: 0, fontSize: 12 }}>
          {t('The pen is emptied. Counts already made in it stay in the record, and the generation is dated when its last pen goes.')}
        </p>
      )}

      <div>
        <button className="btn primary"
          disabled={busy || (maal === 'stock' && !navn.trim())}
          onClick={() => onGo({
            on, part, displace,
            /* Only when the chosen boat still matches the date on screen.
               Editing the date by hand after picking Hordagut means the
               harvest is no longer the one Hordagut did, and filing the name
               anyway would put a boat at a berth it was never at. */
            vessel_name: baat && baat.on === on ? baat.vessel_name : null,
            mmsi: baat && baat.on === on ? baat.mmsi : null,
            n_fish: part === 'some' && antall.trim()
              ? Number(antall.replace(/\s/g, '')) : null,
            reason, method,
            name: navn.trim(), origin: origin.trim() || null,
          })}>
          {busy ? t('Saving…')
            : maal === 'harvest' ? t('Empty the pen')
              : maal === 'treatment' ? t('Record the delousing')
                : maal === 'stock' ? t('Put them in')
                  : t('Move')}
        </button>
      </div>
    </div>
  )
}
